import { Link } from 'react-router-dom';
import {
  Phone,
  Cross,
  Mountain,
  Signal,
  MapPin,
  Compass,
  Type,
  Save,
  ArrowRight,
} from 'lucide-react';

/*
 * SafetyPanel — the reusable Safety & Emergency block.
 *
 * Reused on:
 *   • /safety                    (no site — generic guidance)
 *   • /campsites/:slug           (site prop → location-specific data)
 *
 * Design brief (from the founder):
 *   - High contrast, big tap targets, bold numbers.
 *   - Function over decoration. Readable in rain, low light and stress.
 *   - Warm site palette — no harsh warning reds.
 *   - Reassuring tone. Estate warden, not siren.
 */

const INTRO =
  'The Highlands are beautiful but remote. A few minutes of preparation means you can enjoy them with complete peace of mind.';

function ContactCard({ icon: Icon, label, number, sub, detail, placeholder, href, testId }) {
  const numberClass = placeholder
    ? 'text-2xl italic text-[var(--brand-muted)]'
    : 'text-4xl sm:text-5xl text-[var(--brand-forest-deep)]';

  const inner = (
    <div className="flex h-full flex-col bg-[var(--brand-surface)] border-2 border-[var(--brand-forest-deep)]/15 border-l-8 border-l-[var(--brand-gold)] px-5 py-5 sm:px-6 sm:py-6 transition-shadow duration-200 hover:shadow-[0_8px_28px_-14px_rgba(20,38,24,0.45)]">
      <div className="flex items-start gap-4">
        <span
          aria-hidden="true"
          className="shrink-0 w-14 h-14 grid place-items-center bg-[var(--brand-forest-deep)] text-[var(--brand-gold)] border-2 border-[var(--brand-gold)]"
        >
          <Icon size={26} strokeWidth={2.25} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)]">
            {label}
          </div>
          <div
            className={`mt-1 font-display font-semibold leading-none ${numberClass}`}
          >
            {number}
          </div>
          {sub && (
            <div className="mt-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold text-[var(--brand-olive)]">
              {sub}
            </div>
          )}
        </div>
      </div>
      {detail && (
        <p className="mt-4 text-sm text-[var(--brand-ink)] leading-relaxed font-medium">
          {detail}
        </p>
      )}
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        data-testid={testId}
        className="block"
      >
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testId}>
      {inner}
    </div>
  );
}

function LocationStep({ step, icon: Icon, title, body, example }) {
  return (
    <div className="relative bg-[var(--brand-parchment)] border-2 border-[var(--brand-forest-deep)]/15 px-5 py-5 sm:px-6 sm:py-6">
      <div className="flex items-start gap-4">
        <span
          aria-hidden="true"
          className="shrink-0 w-11 h-11 grid place-items-center bg-[var(--brand-forest-deep)] text-[var(--brand-gold)] font-display text-xl font-bold border-2 border-[var(--brand-gold)]"
        >
          {step}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[var(--brand-forest-deep)]">
            <Icon size={16} strokeWidth={2.25} />
            <h4 className="font-display text-xl font-semibold">{title}</h4>
          </div>
          <p className="mt-2 text-sm text-[var(--brand-ink)] leading-relaxed">
            {body}
          </p>
          {example && (
            <div className="mt-3 inline-flex items-center gap-2 bg-[var(--brand-forest-deep)] text-[var(--brand-gold)] px-3 py-1.5 font-mono text-sm font-bold tracking-wider">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-gold)]/70 not-italic">
                e.g.
              </span>
              {example}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SafetyPanel({ site }) {
  // Assemble the contact cards. Site-specific values override the generics.
  const cards = [
    {
      id: 'emergency-services',
      icon: Phone,
      label: 'Emergency Services',
      number: '999',
      sub: 'or 112 from mobile',
      detail:
        'Mountain Rescue, Ambulance, Police, Fire, Coastguard. Ask for the service you need — the operator will connect you.',
    },
    {
      id: 'nhs-24',
      icon: Cross,
      label: 'NHS 24 · Non-emergency',
      number: '111',
      sub: '24-hour medical advice',
      detail:
        'Free advice for anything urgent but not life-threatening — a bad sprain, feeling unwell in the tent, a cut that won’t settle.',
    },
  ];

  if (site?.safety?.mountainRescue) {
    const mr = site.safety.mountainRescue;
    cards.push({
      id: 'mountain-rescue',
      icon: Mountain,
      label: 'Local Mountain Rescue',
      number: mr.team,
      sub: mr.instruction,
      detail: mr.note,
    });
  } else {
    cards.push({
      id: 'mountain-rescue',
      icon: Mountain,
      label: 'Mountain Rescue',
      number: '999',
      sub: 'Ask for Police, then Mountain Rescue',
      detail:
        'Every Highland call-out routes through 999. The operator will connect you to the nearest team for your grid reference.',
    });
  }

  if (site?.safety?.medical) {
    const med = site.safety.medical;
    cards.push({
      id: 'nearest-medical',
      icon: MapPin,
      label: 'Nearest A&E',
      number: med.name,
      sub: `${med.distance} · ${med.postcode}`,
      detail:
        med.placeholder
          ? 'We’re confirming the closest medical service for this site. In an emergency dial 999.'
          : 'Route planning tip: save the postcode to your maps app before you leave signal.',
      placeholder: med.placeholder,
    });
  } else {
    cards.push({
      id: 'nearest-medical',
      icon: MapPin,
      label: 'Local Highland Medical Services',
      number: 'Coming soon',
      sub: 'GPs & nearest A&E, patch by patch',
      detail:
        'We’re compiling a trusted directory of local GPs and A&E departments for the areas we ride most.',
      placeholder: true,
    });
  }

  if (site?.safety?.phoneSignal) {
    cards.push({
      id: 'phone-signal',
      icon: Signal,
      label: 'Nearest phone signal',
      number: 'On-site notes',
      sub: 'Where the bars come and go',
      detail: site.safety.phoneSignal,
    });
  }

  return (
    <section
      data-testid={site ? 'campsite-safety-panel' : 'safety-panel'}
      className="bg-[var(--brand-parchment-warm)]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 sm:py-16">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-[var(--brand-forest-deep)] bg-[var(--brand-gold)] px-3 py-1.5 border-2 border-[var(--brand-forest-deep)]">
            Safety &amp; Emergency
          </span>
          {site && (
            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--brand-olive)]">
              {site.region}
            </span>
          )}
        </div>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[var(--brand-forest-deep)] leading-tight">
          Peace of mind in the{' '}
          <span className="italic text-[var(--brand-forest)]">Highlands.</span>
        </h2>
        <p
          data-testid="safety-panel-intro"
          className="mt-4 text-base sm:text-lg text-[var(--brand-ink)] leading-relaxed max-w-3xl font-medium"
        >
          {INTRO}
        </p>

        {/* Contact grid — big tap targets, high contrast */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4 sm:gap-5">
          {cards.map((c) => (
            <ContactCard
              key={c.id}
              testId={`safety-contact-${c.id}`}
              icon={c.icon}
              label={c.label}
              number={c.number}
              sub={c.sub}
              detail={c.detail}
              placeholder={c.placeholder}
              href={c.href}
            />
          ))}
        </div>

        {/* Location guide */}
        <div className="mt-14">
          <div className="flex items-center gap-3">
            <Compass size={18} className="text-[var(--brand-forest-deep)]" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)]">
              Step-by-step
            </span>
          </div>
          <h3
            data-testid="safety-location-guide-heading"
            className="mt-3 font-display text-2xl sm:text-3xl text-[var(--brand-forest-deep)]"
          >
            How to give your location to emergency services.
          </h3>
          <p className="mt-3 text-sm sm:text-base text-[var(--brand-ink)] leading-relaxed max-w-3xl">
            When you ring 999, the calmer and clearer your location, the faster
            help arrives. Any one of these three works — give the operator
            whichever you have to hand.
          </p>

          <div
            data-testid="safety-location-steps"
            className="mt-6 grid md:grid-cols-3 gap-4 sm:gap-5"
          >
            <LocationStep
              step="1"
              icon={MapPin}
              title="OS Grid Reference"
              body="A six-figure grid reference pinpoints you to 100 metres. Read it from an Ordnance Survey map, or open a free app like OS Maps or ViewRanger — both show your live grid ref at the top of the screen."
              example={site?.safety?.osGrid || 'NN 123 456'}
            />
            <LocationStep
              step="2"
              icon={Type}
              title="What3Words"
              body="Every 3-metre square on earth has three unique words. Open the What3Words app or the website, tap the location arrow, and read the three words aloud to the operator."
              example={site?.safety?.what3words || '///filled.count.soap'}
            />
            <LocationStep
              step="3"
              icon={Compass}
              title="Describe your surroundings"
              body="If tech fails, plain words win. Nearest landmark, water feature, path junction, direction of the last waymark. Anything that helps a rescue team narrow it down."
            />
          </div>

          {/* Save-before-signal callout */}
          <div className="mt-6 flex items-start gap-3 bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] px-5 py-4 border-l-8 border-[var(--brand-gold)]">
            <span
              aria-hidden="true"
              className="shrink-0 w-8 h-8 grid place-items-center bg-[var(--brand-gold)] text-[var(--brand-forest-deep)]"
            >
              <Save size={16} strokeWidth={2.5} />
            </span>
            <p className="text-sm sm:text-base font-medium leading-relaxed">
              Save your campsite&rsquo;s grid reference and What3Words{' '}
              <span className="font-bold text-[var(--brand-gold)]">
                before you lose signal.
              </span>{' '}
              A screenshot in your camera roll is quicker than any app when the
              phone&rsquo;s wet and the wind&rsquo;s up.
            </p>
          </div>
        </div>

        {/* Coming Home callout — link back to /guides */}
        <Link
          to="/guides"
          data-testid="safety-coming-home-callout"
          className="group mt-12 block relative overflow-hidden bg-[var(--brand-forest)] text-[var(--brand-parchment)] p-6 sm:p-8 transition-shadow duration-200 hover:shadow-[0_10px_32px_-14px_rgba(20,38,24,0.55)]"
        >
          <div className="grain absolute inset-0 opacity-25" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
            <div className="flex-1">
              <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--brand-gold)]">
                Coming Home guide
              </span>
              <h4 className="mt-2 font-display text-2xl sm:text-3xl">
                See our Coming Home guide for tips on wrapping up your ride
                safely.
              </h4>
            </div>
            <span className="btn-gold shrink-0 inline-flex px-6 py-3 text-sm font-semibold tracking-wide items-center gap-2 rounded-none self-start sm:self-auto">
              Read the guide
              <ArrowRight
                size={14}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
}
